// BILDEKØEN — den ENE skjermen der konfidensen kan bli høy.
//
// ══ HVORFOR DENNE SKJERMEN FINNES ══════════════════════════════════════════
// Kontrakten setter et HARDT tak på 0,45 for klassifisering fra seismikk og
// magnetisme (`MAKS_KONFIDENS_SEISMISK`), og begrunnelsen er verdt å gjenta:
// kjeden spektralinnhold → periodisitet → skrittfrekvens → «person» er en
// antakelse i hvert ledd, og en ELG i tungt terreng gir samme signatur som en
// person — bare sterkere, siden den veier mer. Derfor kan ingen mengde
// utløsninger gjøre et seismisk «person» til noe man handler på.
//
// Det finnes ÉN vei over taket, og den går gjennom denne skjermen: et menneske
// som ser bildet. Da er klassifiseringen en OBSERVASJON og ikke en slutningskjede,
// og kontrakten gir den 0,9 (`KONFIDENS_VURDERT_BILDE`). Et uåpnet bilde ligger på
// 0,2 — ikke fordi bildet er dårlig, men fordi ingen vet hva som er på det.
//
// ══ DEN HARDE REGELEN: INGEN VURDERING UTEN Å HA SETT BILDET ═══════════════
// Kontrakten avviser `menneskeligVurdering` uten `vurdertAvMenneske: true`, med
// begrunnelsen at det ellers er «en maskinklassifisering som utgir seg for å være
// en observasjon». Denne skjermen håndhever det samme ett steg tidligere: KAN
// klienten ikke vise bildet, er vurderingsknappene DEAKTIVERT, med årsaken
// skrevet ut. Alternativet — å la jegeren trykke «person» på et bilde han ikke har
// sett — ville produsert nettopp den falske observasjonen, og den ville deretter
// blitt sendt til SKADE med konfidens 0,9.
//
// Bildet hentes fra en mediatjeneste klienten er konfigurert med (`VITE_ULL_MEDIA`
// → `ULL_MEDIA`), aldri fra en URL i dataene: `BildeReferanse` i kontrakten
// avviser aktivt `://`, `@` og `/` i en bildeId, slik at ingen kan lekke et internt
// vertsnavn eller en signert URL gjennom et felt som heter «bilde».
//
// ══ «BEKREFTET» SKRIVES ALDRI FØR NOEN HAR TRYKKET ═════════════════════════
// Køen viser status i tre trinn, og de holdes strengt fra hverandre:
//   * IKKE SETT      — kontraktens `vurdertAvMenneske: false`. Standard.
//   * SETT AV DEG    — jegeren har trykket. Vurderingen er ekte (et menneske SÅ
//                      bildet), men broen har ikke kvittert. Står det slik, vet
//                      jegeren at SKADE ennå ikke har den.
//   * SETT, KVITTERT — broen har sendt utløsningen tilbake med
//                      `vurdertAvMenneske: true`. Først nå er den alles sannhet.
import {
  KONFIDENS_VURDERT_BILDE, MAKS_KONFIDENS_SEISMISK, MAKS_KONFIDENS_UVURDERT_BILDE, ugsKlassifiser,
  type UgsMalklasse, type UgsSensor, type UgsUtlosningBilde,
} from '@skade/contracts';
import {
  div, felt, formaterAlder, formaterKlokke, h, knapp, merke, nv, p, sett, span, tomt,
} from '@skade/sensorflate';
import { BILDEARSAK_NAVN, LYSFORHOLD_NAVN, MALKLASSE_NAVN, TOMT_BILDE_BETYR } from '../lib/format.js';
import { hendelserITid, kjenteSensorer } from '../lib/lager.js';
import { SENSORART_NAVN, type Ullhendelse } from '../api/typer.js';
import { ART_IKON, ikon } from '../ui/ikoner.js';
import type { Kontekst } from './kontekst.js';

/**
 * Klassene jegeren kan velge.
 *
 * `ukjent` er BEVISST UTELATT som knapp, selv om den finnes i `UgsMalklasse`. Å
 * velge den ville satt `menneskeligVurdering: 'ukjent'`, og kontrakten gir DEN
 * konfidens 0,9 — altså «jeg er 90 % sikker på at det er ukjent», som ikke betyr
 * noe. Det jegeren egentlig vil si er «jeg har sett bildet og klarer ikke å
 * avgjøre», og det er en EGEN tilstand i kontrakten: `vurdertAvMenneske: true`
 * UTEN vurdering, som gir 0,3 og begrunnelsen «en reell observasjon av uavklart,
 * ikke manglende arbeid». Knappen «Kan IKKE avgjøre» treffer den.
 */
const KLASSEVALG: readonly UgsMalklasse[] = ['person-til-fots', 'person-lopende', 'hjul', 'belte', 'dyr'];

export interface Bildesammendrag {
  ikkeSett: number;
  settAvDeg: number;
  kvittert: number;
}

interface Bilderad {
  hendelse: Ullhendelse;
  bilde: UgsUtlosningBilde;
  sensor: UgsSensor | null;
}

export function tegnBilde(vert: HTMLElement, ctx: Kontekst): Bildesammendrag {
  const t = ctx.tilstand();
  const naa = ctx.naa();
  const sensorKart = new Map(kjenteSensorer(t).map((s) => [s.sensorId, s]));

  const rader: Bilderad[] = hendelserITid(t)
    .filter((h2): h2 is Ullhendelse & { utlosning: UgsUtlosningBilde } => h2.utlosning.maling === 'bilde')
    .map((h2) => ({ hendelse: h2, bilde: h2.utlosning, sensor: sensorKart.get(h2.sensorId) ?? null }))
    // NYESTE FØRST. En kø jobbes normalt gjennom fra eldste, men her er det
    // motsatt vei som er riktig: det ferskeste bildet er det eneste man fortsatt
    // kan handle på. Etterslepet står som et tall i toppen, så det ikke glemmes.
    .reverse();

  const kvittert = rader.filter((r) => r.bilde.vurdertAvMenneske);
  const lokale = rader.filter((r) => !r.bilde.vurdertAvMenneske && ctx.bildevurdering(r.hendelse.id));
  const kø = rader.filter((r) => !r.bilde.vurdertAvMenneske && !ctx.bildevurdering(r.hendelse.id));

  if (rader.length === 0) {
    const kameraer = t.sensorer.filter((s) => s.art === 'kamera').length;
    sett(
      vert,
      tomt(
        'Ingen bilder',
        kameraer === 0
          ? 'Ingen kamera er lagt ut. Et kamera er den eneste UGS-en som gir noe et menneske kan verifisere med egne øyne — alt annet i denne appen er en slutning.'
          : `${kameraer} kamera er lagt ut, men ingen har utløst ennå. Bildene kommer hit som en kø når de gjør det.`,
        { ikon: 'kamera' },
      ),
    );
    return { ikkeSett: 0, settAvDeg: 0, kvittert: 0 };
  }

  const rot = div('bil');
  rot.append(tegnTopp(kø.length, lokale.length, kvittert.length));
  if (!ctx.kanViseBilde()) rot.append(tegnManglerMedia());

  if (kø.length > 0) {
    const bolk = div('bil-bolk');
    bolk.append(h(3, 'bil-bolkhode', `IKKE SETT — venter på et menneske (${kø.length})`));
    for (const r of kø) bolk.append(tegnRad(r, ctx, naa));
    rot.append(bolk);
  }
  if (lokale.length > 0) {
    const bolk = div('bil-bolk');
    bolk.append(h(3, 'bil-bolkhode', `SETT AV DEG — broen har ikke kvittert (${lokale.length})`));
    for (const r of lokale) bolk.append(tegnRad(r, ctx, naa));
    rot.append(bolk);
  }
  if (kvittert.length > 0) {
    const bolk = div('bil-bolk');
    bolk.append(h(3, 'bil-bolkhode', `SETT OG KVITTERT (${kvittert.length})`));
    for (const r of kvittert) bolk.append(tegnRad(r, ctx, naa));
    rot.append(bolk);
  }

  sett(vert, rot);
  return { ikkeSett: kø.length, settAvDeg: lokale.length, kvittert: kvittert.length };
}

// ── Topp ────────────────────────────────────────────────────────────────────

function tegnTopp(kø: number, lokale: number, kvittert: number): HTMLElement {
  const rot = div('bil-topp');
  const tall = div('bil-tallrad');
  tall.append(felt('Ikke sett', String(kø), 'kø'));
  tall.append(felt('Sett av deg', String(lokale)));
  tall.append(felt('Kvittert', String(kvittert)));
  rot.append(tall);
  rot.append(p(
    'bil-brød',
    `Et uåpnet bilde har konfidens ${MAKS_KONFIDENS_UVURDERT_BILDE} — kameraet utløste, innholdet er ukjent. Har et menneske sett det, `
    + `blir den ${KONFIDENS_VURDERT_BILDE}. Det er den ENESTE veien over taket på `
    + `${String(MAKS_KONFIDENS_SEISMISK).replace('.', ',')} som gjelder for alt annet i denne appen.`,
  ));
  return rot;
}

/**
 * Om «Hvorfor?»-utvidelsen under står åpen. Modulnivå med vilje: hele flata
 * tegnes om hvert sekund, og en `<details>` som smeller igjen ett sekund etter
 * at jegeren åpnet den, er verre enn ingen utvidelse.
 */
let hvorforÅpen = false;

/**
 * Den ærlige tomtilstanden når mediatjenesten ikke er satt opp.
 *
 * Den er en INFO-boks og ikke en feil, av samme grunn som `manglerKilde` i pakken:
 * ingenting er i stykker, tjenesten er ikke rullet ut. Men konsekvensen er hard og
 * står først — knappene virker ikke, og de skal ikke virke.
 *
 * UX-RAPPORTEN (J3): forklaringen sto som en tett teknisk/juridisk tekstblokk,
 * og en tekstblokk ingen leser forklarer ingenting. Nå står KONSEKVENSEN som én
 * synlig linje, og hele den opprinnelige begrunnelsen — uendret — bak
 * «Hvorfor?». Ingenting er fjernet, bare flyttet bak et klikk.
 */
function tegnManglerMedia(): HTMLElement {
  const rot = div('bil-mangler');
  const hode = div('bil-hullhode');
  hode.append(ikon('info'));
  hode.append(h(3, 'bil-bolkhode', 'Bilde ikke tilgjengelig på denne klienten — vurdering krever at bildet er sett'));
  rot.append(hode);

  const det = document.createElement('details');
  det.className = 'bil-hvorfor';
  det.open = hvorforÅpen;
  det.addEventListener('toggle', () => { hvorforÅpen = det.open; });
  const sum = document.createElement('summary');
  sum.textContent = 'Hvorfor?';
  det.append(sum);
  det.append(p(
    'bil-brød',
    'Mediatjenesten som slår opp bildeId → bilde er ikke konfigurert (VITE_ULL_MEDIA er ikke satt ved bygg). '
    + 'Kontrakten inneholder med vilje INGEN bilde-URL — en opak id, som klienten selv slår opp bak rettighetskontroll.',
  ));
  det.append(p(
    'bil-brød',
    'Derfor er vurderingsknappene DEAKTIVERT. En vurdering avgitt uten å ha sett bildet ville vært en maskinklassifisering '
    + 'som utgir seg for å være en observasjon — og den ville deretter gått til SKADE med høy konfidens. '
    + 'Metadataene under er fortsatt ekte og brukbare: tidspunkt, utløsningsårsak og lysforhold sier noe om bildet uten at det vises.',
  ));
  rot.append(det);
  return rot;
}

// ── Én rad ──────────────────────────────────────────────────────────────────

function tegnRad(r: Bilderad, ctx: Kontekst, naaMs: number): HTMLElement {
  const b = r.bilde;
  const lokal = ctx.bildevurdering(r.hendelse.id);
  const status = b.vurdertAvMenneske ? 'kvittert' : lokal ? 'lokal' : 'ikke-sett';
  const rot = div(`bil-rad bil-rad-${status}`);

  const hode = div('bil-hullhode');
  if (r.sensor) hode.append(ikon(ART_IKON[r.sensor.art]));
  hode.append(span('bil-navn', r.sensor?.navn ?? r.hendelse.sensorId));
  if (r.sensor) hode.append(merke(SENSORART_NAVN[r.sensor.art], `art-${r.sensor.art}`));
  hode.append(merke(
    status === 'kvittert' ? 'SETT OG KVITTERT' : status === 'lokal' ? 'SETT AV DEG' : 'IKKE SETT',
    `bilde-${status}`,
  ));
  if (r.hendelse.ovelse) hode.append(merke('ØVING', 'ovelse'));
  hode.append(div('bil-rom'));
  hode.append(span('bil-klokke', `${formaterKlokke(r.hendelse.sisteTsMs)} · ${formaterAlder(Math.max(0, naaMs - r.hendelse.sisteTsMs))}`));
  rot.append(hode);

  rot.append(tegnBilderute(b, ctx));

  rot.append(nv('Bilde-id', b.bildeId, 'grid'));
  rot.append(nv('Antall bilder', String(b.antallBilder)));
  rot.append(nv('Utløsningsårsak', BILDEARSAK_NAVN[b.utlosningsarsak]));
  rot.append(nv('Lysforhold', b.lysforhold ? LYSFORHOLD_NAVN[b.lysforhold] : 'Ikke oppgitt av noden'));
  // Hva et TOMT bilde betyr, gitt årsaken. Det er ikke pedanteri: et PIR-utløst
  // bilde uten folk på betyr at noe varmt beveget seg og var ute av bildeflaten.
  rot.append(p('bil-note', TOMT_BILDE_BETYR[b.utlosningsarsak]));

  // Kontraktens egen klassifisering av utløsningen, ORDRETT. For et uåpnet bilde
  // sier den selv at ingen har sett det — det er teksten som skal stå her, ikke
  // vår omskrivning av den.
  const kl = ugsKlassifiser(b, r.sensor ?? undefined);
  rot.append(p('bil-klassifisering', kl.begrunnelse));

  if (b.vurdertAvMenneske) {
    rot.append(nv(
      'Menneskets vurdering',
      b.menneskeligVurdering
        ? `${MALKLASSE_NAVN[b.menneskeligVurdering]} — av ${b.vurdertAv ?? 'ukjent'}`
        : `Sett, men klassen kunne IKKE avgjøres — av ${b.vurdertAv ?? 'ukjent'}`,
      'ok',
    ));
    return rot;
  }

  if (lokal) {
    rot.append(nv(
      'Din vurdering',
      lokal.avvist
        ? `Sett — kunne IKKE avgjøre klassen (${formaterKlokke(lokal.tsMs)})`
        : `${MALKLASSE_NAVN[lokal.klasse!]} (${formaterKlokke(lokal.tsMs)})`,
    ));
    // TRE utfall, ikke to. «Ligger i køen» ville vært en LØGN under en øving:
    // øvingsdata kommer ikke inn i køen i det hele tatt (krav 4 i
    // `@skade/sensorflate/ovelse.ts`), og en jeger som tror vurderingen ligger klar
    // til neste sendevindu ville ventet på noe som aldri kommer.
    rot.append(p(
      'bil-note',
      ctx.sending().ovelse
        ? 'ØVING: vurderingen er lagret LOKALT i denne økta og ligger IKKE i køen. Ingenting fra en øving '
          + 'kan sendes til SKADE, og den forsvinner når du slår av øvingsdata.'
        : lokal.sendt
          ? 'Sendt til broen. Raden flytter seg til «SETT OG KVITTERT» først når broen sender utløsningen tilbake med vurderingen — det er da SKADE har den.'
          : 'IKKE sendt: sending er slått av (EMCON). Vurderingen ligger i køen og går ut når du slår på sending.',
    ));
    return rot;
  }

  rot.append(tegnKnapper(r, ctx));
  return rot;
}

/**
 * Bilderuta. Tre utfall, og alle tre er ærlige:
 *   * mediatjeneste konfigurert → bildet vises
 *   * bildet finnes ikke / feiler → en linje som sier at OPPSLAGET feilet, ikke
 *     en ødelagt bilderamme
 *   * ingen mediatjeneste → en dempet rute som sier hvorfor det ikke er noe å se
 */
function tegnBilderute(b: UgsUtlosningBilde, ctx: Kontekst): HTMLElement {
  const rute = div('bil-rute');
  const adresse = ctx.bildeadresse(b.bildeId);
  if (!adresse) {
    rute.classList.add('bil-rute-tom');
    rute.append(ikon('kamera'));
    rute.append(span('bil-rutetekst', 'Ingen mediatjeneste — bildet kan ikke vises'));
    return rute;
  }
  const img = document.createElement('img');
  img.className = 'bil-bilde';
  img.loading = 'lazy';
  img.decoding = 'async';
  // Alt-teksten er ikke pynt: for den som bruker skjermleser er dette hele
  // informasjonen om at det finnes et bilde, og at innholdet ikke er kjent.
  img.alt = `Uvurdert kamerabilde ${b.bildeId} fra UGS. Innholdet er ikke beskrevet — det er nettopp det som skal vurderes.`;
  img.src = adresse;
  img.addEventListener('error', () => {
    const feil = div('bil-rute bil-rute-feil');
    feil.append(ikon('varsel'));
    feil.append(span('bil-rutetekst', `Oppslaget av bilde ${b.bildeId} feilet. Bildet finnes kanskje ikke ennå, eller rettigheten mangler.`));
    img.replaceWith(feil);
  });
  rute.append(img);
  return rute;
}

/**
 * Vurderingsknappene.
 *
 * Alle deaktiveres når bildet ikke kan vises, med årsaken i `title` OG som en
 * synlig linje under — en deaktivert knapp uten forklaring er den verste
 * varianten, fordi jegeren tror appen er i stykker.
 */
function tegnKnapper(r: Bilderad, ctx: Kontekst): HTMLElement {
  const rot = div('bil-knapper');
  const kanSe = ctx.kanViseBilde();
  const sperre = kanSe
    ? null
    : 'Bildet kan ikke vises på denne klienten, og da kan det ikke vurderes. Se forklaringen øverst.';

  rot.append(span('bil-knappetikett', 'Hva ser du på bildet?'));
  const rad = div('bil-knapperad');
  for (const klasse of KLASSEVALG) {
    rad.append(knapp(MALKLASSE_NAVN[klasse], () => {
      ctx.vurderBilde(r.hendelse.id, klasse);
    }, { variant: 'stille', deaktivert: !kanSe, ...(sperre ? { tittel: sperre } : {}) }));
  }
  rad.append(knapp('Kan IKKE avgjøre', () => {
    ctx.vurderBilde(r.hendelse.id, null);
  }, {
    variant: 'stille',
    deaktivert: !kanSe,
    tittel: sperre ?? 'Du har SETT bildet, men klarer ikke å avgjøre klassen. Det er en reell observasjon av «uavklart», ikke manglende arbeid.',
  }));
  rot.append(rad);
  if (sperre) rot.append(p('bil-sperre', sperre));
  return rot;
}
